import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useLocation } from "react-router-dom";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { DOCUMENT_FORMAT_MAPPER } from "../../../../../pages/app/provider/patient/patient-list/patient-chart-new/document/document-constant";
import { useEducationMaterialCreate } from "../../../../../queries/education-material/use-education-material-create";
import { useEducationMaterialUpdate } from "../../../../../queries/education-material/use-education-material-update";
import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import {
  showToastOnApiCall,
  getXtenantId,
  DEF_PAGINATION,
  DGT_Pagination,
} from "../../../../../utils/utils";
import { useDebounce } from "@/hooks/use-debounce";
import { useState } from "react";
import { AsyncSelectValue } from "../../../../AsyncSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import CustomTextArea from "../../../../CustomTextArea";
import Input from "../../../../Input";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import StyledFormModal from "../../../../globalModal/StyledModal";
import StyledButtonV2 from "../../../StyledButtonV2";
import DropZone from "../../../DropZone";
import {
  EducationMaterial,
  INITIAL_VALUES,
  // SUPPORTED_FORMATS,
  validationSchema,
} from "./education-constant";
import SupportedFileFormats from "./SupportedFileFormats";
interface UpserEducationDrawerProps {
  disclosureHost: DisclosureHost;
}

const UpsertEducationDrawer = ({
  disclosureHost,
}: UpserEducationDrawerProps) => {
  const { mutateAsync: createEducationMaterial } = useEducationMaterialCreate();
  const { mutateAsync: updateEducationMaterial } = useEducationMaterialUpdate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const location = useLocation();
  const refetchEducationMaterialList = () => {
    queryClient.invalidateQueries({
      queryKey: ["education-material-list"],
    });
  };

  const saveEducationMaterial = async (values: any) => {
    const payload = {
      ...values,
      isEditFile: undefined,
      documentDate: new Date().toISOString(),
      providerId: values.providerId?.value,
      xTENANTID: getXtenantId() ?? "",
    };

    showToastOnApiCall({
      apiCall: disclosureHost?.data
        ? updateEducationMaterial(payload)
        : createEducationMaterial(payload),
      showSnackbar,
      onSave: () => {
        disclosureHost.close();
        formik.resetForm();
        refetchEducationMaterialList();
      },
    });
  };

  const [me] = useCurrentUser();

  const formik = useFormik<EducationMaterial>({
    initialValues: disclosureHost?.data || {
      ...INITIAL_VALUES,
      providerId: {
        value: me?.uuid,
        label: me?.firstName + " " + me?.lastName,
      },
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: saveEducationMaterial,
  });

  const [specialtyPagination, setSpecialtyPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [specialtySearchKey, setSpecialtySearchKey] = useDebounce<string>("");
  const { data: specialtiesList, isFetching: specialtiesFetching } =
    useSpecialtiesList({
      enabled: disclosureHost.opened,
      pagination: specialtyPagination,
      searchString: specialtySearchKey,
    });

  const isDisabled =
    location.pathname === PROVIDER_PATHS.LIBRARY_EDUCATION_MATERIAL;

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle={
        disclosureHost?.data
          ? "Edit Education Material"
          : "Upload Education Material"
      }
      dialogAction={
        <StyledButtonV2
          label="Upload"
          variant="contained"
          onClick={() => formik.handleSubmit()}
          sx={{ width: "100px" }}
          disabled={formik.isSubmitting}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "38vw",
          height: "80vh",
        },
      }}
    >
      {/* w={"38vw"} */}
      {/* open={opened} */}
      {/* onClose={() => { */}
      {/*   close(); */}
      {/*   formik.resetForm(); */}
      {/* }} */}
      <Grid container spacing={2} pt={2}>
        <Grid item xs={12} sm={6}>
          <Input
            label="Document Title"
            required
            textFieldProps={{
              placeholder: "Enter Document Title",
              name: "name",
              value: formik.values.name,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            }}
            error={(formik.touched.name && formik.errors.name) as any}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <ProviderSelectScroll
            required
            label="Uploaded By"
            value={formik.values.providerId as AsyncSelectValue}
            onChange={(v: any) => formik.setFieldValue("providerId", v)}
            disabled={isDisabled}
            error={
              (formik.touched.providerId && formik.errors.providerId) as any
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label="Condition"
            textFieldProps={{
              name: "condition",
              value: formik.values.condition,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Enter Condition",
            }}
            error={formik.touched.condition && formik.errors.condition}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6}>
        <CustomDatePicker
          label="Date"
          //   required
          value={formik.values.documentDate}
          onChange={(val) =>
            formik.setFieldValue(
              "documentDate",
              val ? val.toISOString() : null
            )
          }
          //   error={formik.touched.date && formik.errors.date}
          placeholder="Choose Date"
          name="documentDate"
          handleBlur={formik.handleBlur}
        />
      </Grid> */}

        <Grid item xs={12} sm={6}>
          <CustomSelectScroll
            label="Specialty"
            name="specialityId"
            value={formik.values?.specialityId}
            onChange={(val: any) => formik.setFieldValue("specialityId", val)}
            options={specialtiesList?.data?.content?.map((specialty) => ({
              value: specialty?.uuid,
              label: specialty?.name,
            }))}
            loading={specialtiesFetching}
            pagination={specialtyPagination}
            setPagination={setSpecialtyPagination}
            totalElements={specialtiesList?.data?.page?.totalElements}
            onSearch={(_e, val) => {
              setSpecialtySearchKey(val);
              setSpecialtyPagination((p) => ({ ...p, page: 0 }));
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Document Description"
            //   required
            minRows={2}
            name="description"
            placeholder="Enter Document Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            //   error={formik.touched.description && formik.errors.description}
          />
        </Grid>

        <Grid item xs={12} mt={2}>
          <DropZone
            // supportedFile={SUPPORTED_FORMATS}
            value={formik.values?.fileName}
            height={"243px"}
            accept="all"
            content="Drag & Drop files Or Browse Files"
            onClick={(file) => {
              const curFile = file?.item(0);

              if (curFile) {
                formik.setFieldValue("file", curFile);
                formik.setFieldValue("fileName", curFile?.name);
              } else {
                formik.setFieldValue("file", null);
                formik.setFieldValue("fileName", null);
              }

              if (curFile?.type) {
                formik.setFieldValue(
                  "documentFormat",
                  DOCUMENT_FORMAT_MAPPER?.[curFile.type] ?? "OTHER"
                );
                formik.validateForm();
                formik.setFieldTouched("file", !curFile);
              }
            }}
            error={formik.touched.file && (formik.errors.file as any)}
          />
        </Grid>
        <Grid item xs={12}>
          <SupportedFileFormats />
        </Grid>
      </Grid>
    </StyledFormModal>
  );
};

export default UpsertEducationDrawer;
